import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { FETCH_DATA_TAG_REPORTS, CLEAR_DATA_TAG_SNACKBAR, DOWNLOAD_DATA_TAG_REPORT, VIEW_DATA_TAG_REPORT, CLEAR_UPLOAD_STATE, DATA_TAG_REPORTS_BASE_URL } from './DataTagConstants';
import "../../components/views/GenerateReport/Report.scss";
import Page_Loader from "../../assets/vizr_images/page_loader.gif";
import { Button, Input, makeStyles, CircularProgress, Backdrop } from '@material-ui/core';
import CloseIcon from '../../assets/vizr_images/ic_remove.svg';
import SearchIcon from '../../assets/vizr_images/ic_search.svg';
import { errorSnackbar, expiryDaysCalculation, getExpiryDetails } from '../../components/common/utilities';
import EnhancedTable from '../../components/shared/Table/Table';
import { DataTagReportsHeadCells } from "../../components/common/headCells";
import DownloadIcon from '@material-ui/icons/GetApp';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import WatchLaterIcon from '@material-ui/icons/WatchLater';
import { formatDateAndTime } from '../../components/views/GenerateReport/utilities';
import UploadReportModal from './UploadReportModal';
import { isEmpty } from 'lodash';
import viewReport from "../../assets/vizr_images/viewReport.svg";
import viewReportDisabled from "../../assets/vizr_images/viewReportDisabled.svg";
import { Popover } from '@mui/material';

const useStyles = makeStyles((theme) => ({
  reportSearchInput: {
    width: "350px",
    "& input": {
      padding: "5px 0px 5px 16px",
      '&::placeholder': {
        color: '#081981 !important',
        opacity: 1,
        fontSize: "16px",
      },
    }
  },
  uploadButton: {
    backgroundColor: '#081981',
    color: 'white',
    textTransform: 'none',
    padding: '8px 24px',
    '&:hover': {
      backgroundColor: '#050f5a',
    },
  }
}));

function DataTagReports() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [searchReportName, setSearchReportName] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [recordsPerPage, setRecordsPerPage] = useState(20);
  const [openSnackBar, setOpenSnackBar] = useState(false);
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [openUploadSnackBar, setOpenUploadSnackBar] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const showIcon = localStorage.getItem("user_role")?.includes("Administrator") ? true : false;

  const reportStatusMessages = {
    "Success": "Success",
    "In-Progress": "In Progress",
    "Not-Connected": "Not Connected",
    "Failure": "Failure",
  };

  const reportFiles = useSelector(
    (state) => state.DataTagReportsReducer.allReports
  );

  const loader = useSelector(
    (state) => state.DataTagReportsReducer.loader
  );

  const downloadLoader = useSelector(
    (state) => state.DataTagReportsReducer.downloadLoader
  );

  const snackBarMessage = useSelector(
    (state) => state.DataTagReportsReducer.snackBarMessage
  );

  const uploadSnackBarMessage = useSelector(
    (state) => state.DataTagReportsReducer.uploadSnackBarMessage
  );

  useEffect(() => {
    // Fetch all reports once on component mount
    dispatch({ type: FETCH_DATA_TAG_REPORTS });
    
    return () => {
      setOpenSnackBar(false);
      dispatch({ type: CLEAR_DATA_TAG_SNACKBAR })
    }
  }, [])
  // Client-side filtering and pagination
  const { totalReportsCount, dataTagReports } = useMemo(() => {
    // Filter reports based on search query
    const filtered = searchReportName
      ? reportFiles.filter(file =>
          file.reportName?.toLowerCase().includes(searchReportName.toLowerCase()) ||
          file.fileName?.toLowerCase().includes(searchReportName.toLowerCase())
        )
      : reportFiles;

    // Apply pagination
    const startIndex = currentPage * recordsPerPage;
    const endIndex = startIndex + recordsPerPage;
    const paginated = filtered.slice(startIndex, endIndex);

    return {
      totalReportsCount: filtered.length,
      dataTagReports: paginated
    };
  }, [reportFiles, searchReportName, currentPage, recordsPerPage]);

  useEffect(() => {
    if (snackBarMessage && snackBarMessage.status && snackBarMessage.message) {
      setOpenSnackBar(true);
    }
  }, [snackBarMessage]);

  useEffect(() => {
    if (uploadSnackBarMessage && uploadSnackBarMessage.status && uploadSnackBarMessage.message) {
      setOpenUploadSnackBar(true);
    }
  }, [uploadSnackBarMessage]);

  const handlePopUpClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackBar(false);
    dispatch({ type: CLEAR_DATA_TAG_SNACKBAR })
  };

  const handleUploadPopUpClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenUploadSnackBar(false);
    dispatch({ type: CLEAR_UPLOAD_STATE })
  };

  const handleOpenUploadModal = () => {
    setOpenUploadModal(true);
  };

  const handleCloseUploadModal = () => {
    setOpenUploadModal(false);
  };

  const handleChangeRowsPerPage = (value) => {
    setCurrentPage(0);
    setRecordsPerPage(value);
  };

  const handleChangePage = (page) => {
    setCurrentPage(page);
  };

  const renderNull = () => {
    return (<>-</>);
  };

  const renderFileName = (params) => {
    if (!params) {
      return renderNull();
    }
    return (
      <div className="report_titles name" title={params.reportName || params.fileName}>
        {params.reportName || params.fileName}
      </div>
    )
  };

  const renderCreatedDate = (params) => {
    if (!params || !params.createdDate) {
      return renderNull();
    }
    // Parse ISO date string and format in UTC
    const date = new Date(params.createdDate);
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const year = date.getUTCFullYear();
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    
    const formattedDate = `${month}-${day}-${year}`;
    const formattedTime = `${hours}:${minutes}`;
    
    return (
      <div>
        <div className='report_titles'>{formattedDate}</div>
        <div className='report_titles'>{formattedTime}</div>
      </div>
    )
  };

  const reportDate = (dateStr) => {
    if (!dateStr) {
      return '-';
    }
    const date = new Date(dateStr);
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const year = date.getUTCFullYear();
    return `${month}-${day}-${year}`;
  };

  const renderReportPeriod = (params) => {
    if (!params || !params.reportPeriodStartDate || !params.reportPeriodEndDate) {
      return renderNull();
    }
    const startDate = reportDate(params.reportPeriodStartDate);
    const endDate = reportDate(params.reportPeriodEndDate);
    
    return (
      <div className='report_timeRange'>
        <div className='report_titles'>{`${startDate} to ${endDate}`}</div>
      </div>
    )
  };

  const renderSections = (params) => {
    if (!params || !params.sections) {
      return renderNull();
    }
    return (
      <div className='report_titles content' title={params.sections}>
        {params.sections}
      </div>
    )
  };

  const renderGeneratedBy = (params) => {
    if (!params || !params.createdBy) {
      return renderNull();
    }
    return (
      <div className='report_titles generateBy'>{params.createdBy}</div>
    )
  };

  const renderStatus = (params) => {
    if (!params || isEmpty(params.status)) {
      return renderNull();
    }
    
    const status = params.status;
    
    if (status === "Success") {
      return (
        <div title={reportStatusMessages[status]} className="side-icons">
          <CheckCircleIcon className={`report-status ${status}`} fontSize="small" />
        </div>
      );
    } else if (status === "In-Progress" || status === "Not-Started") {
      return (
        <div title={reportStatusMessages[status]} className="side-icons">
          <WatchLaterIcon className={`report-status`} fontSize="small" />
        </div>
      );
    } else if (status === "Failure") {
      return (
        <div title={reportStatusMessages[status]} className="side-icons">
          <CancelIcon className={`report-status ${status}`} fontSize="small" />
        </div>
      );
    } else {
      return renderNull();
    }
  };

  const renderExpiryDate = (params) => {
    if (!params || !params.expirationTime) {
      return renderNull();
    }
    
    const remainingDays = expiryDaysCalculation(params.expirationTime);
    const expiryDetails = getExpiryDetails(remainingDays);
    const expiryMessage = remainingDays < 0 
      ? `Expired ${Math.abs(remainingDays)} days ago`
      : `Expires in ${remainingDays} days`;
    
    return (
      <div>
        <div className="report_table">
          <img src={expiryDetails.icon} alt="expiry" />
          <div className={expiryDetails.colorClassName}>
            {expiryMessage}
            <div>at {formatDateAndTime(params.expirationTime, "MM-DD-YYYY")}</div>
          </div>
        </div>
      </div>
    );
  };

  const handleDownload = (downloadUrl, fileName) => {
    if (!downloadUrl) return;
    
    // Prepend base URL and add mode=download query parameter
    const fullUrl = `${DATA_TAG_REPORTS_BASE_URL}${downloadUrl}`;
    const urlWithMode = fullUrl.includes('?') 
      ? `${fullUrl}&mode=download` 
      : `${fullUrl}?mode=download`;
    
    dispatch({ 
      type: DOWNLOAD_DATA_TAG_REPORT, 
      payload: { downloadUrl: urlWithMode, fileName } 
    });
  };

  const handleViewReport = (rowData) => {
    if (rowData.status === "Success") {
      handleCloseReportPopup();
      
      // Prepend base URL and add mode=view query parameter
      const fullUrl = `${DATA_TAG_REPORTS_BASE_URL}${rowData.downloadUrl}`;
      const urlWithMode = fullUrl.includes('?') 
        ? `${fullUrl}&mode=view` 
        : `${fullUrl}?mode=view`;
      
      // Dispatch action to view report with authentication
      dispatch({ 
        type: VIEW_DATA_TAG_REPORT, 
        payload: { viewUrl: urlWithMode, fileName: rowData.fileName } 
      });
    } else {
      return null;
    }
  };

  const handleOpenReportPopup = (data, event) => {
    setSelectedRow(data.reportId);
    setAnchorEl(event.currentTarget);
  };

  const handleCloseReportPopup = () => {
    setAnchorEl(null);
  };

  const handleViewReportPopUpTitle = (params) => {
    const reportGenerationProgressMessage = {
      "In-Progress": "Report Generation In Progress",
      "Not-Connected": "Report Generation Not Started",
      "Failure": "Report Generation Failed",
    };
    return reportGenerationProgressMessage[params] || "";
  };

  const PopupContent = (rowId) => {
    const rowData = dataTagReports.find((row) => row.reportId === rowId);
    const open = Boolean(anchorEl);
    const id = open ? "simple-popover" : undefined;
    const successStatus = !isEmpty(rowData) && rowData.status === "Success";

    return (
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleCloseReportPopup}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        className="reportRepositoryPopover"
      >
        <div className="viewHistoryPopup">
          <div className="report-popup-content">
            <div className={`popup-images viewIcon ${successStatus ? "Success" : ""}`}>
              <img
                src={successStatus ? viewReport : viewReportDisabled}
                alt="View Report"
                title={successStatus ? "View Report" : handleViewReportPopUpTitle(rowData?.status)}
                onClick={() => handleViewReport(rowData)}
              />
            </div>
            <div className={`popup-images ${successStatus ? "Success" : ""}`}>
              <DownloadIcon
                className={`download-icon-popup data-tag-popover-icon ${!successStatus ? 'disabled' : ''}`}
                onClick={() => successStatus && handleDownload(rowData.downloadUrl, rowData.fileName)}
                titleAccess={successStatus ? "Download Report" : handleViewReportPopUpTitle(rowData?.status)}
              />
            </div>
          </div>
        </div>
      </Popover>
    );
  };

  const renderDownloadAction = (params) => {
    return (
      <div className="side-icons">
        <DownloadIcon
          className="download-icon"
          onClick={() => handleDownload(params.downloadUrl, params.fileName)}
          titleAccess="Download Report"
          style={{ color: '#081981' }}
        />
      </div>
    )
  };

  const handleChange = (e) => {
    setSearchReportName(e.target.value);
  }

  const handleSearch = () => {
    // Reset to first page when searching
    setCurrentPage(0);
  }

  const handleClearSearch = () => {
    setSearchReportName("");
    setCurrentPage(0);
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const renderSearchBar = () => {
    return (
      <div className='repositorySearch'>
        <div className='reportSearchBar'>
          <Input
            value={searchReportName}
            type='text'
            placeholder='Search here...'
            onKeyPress={(event) => handleKeyPress(event)}
            disableUnderline
            className={classes.reportSearchInput}
            onChange={(e) => handleChange(e)}
          />
          <Button
            className="searchCancelIcon"
            onClick={(event) => {
              handleClearSearch()
            }}
            aria-label="close"
          >
            <img src={CloseIcon} alt={'close'} />
          </Button>
          <hr />
          <Button
            className="searchIcon"
            onClick={() => {
              handleSearch()
            }}
            aria-label="search"
          >
            <img src={SearchIcon} alt={'search'} />
          </Button>
        </div>
        {showIcon && (
          <Button
            variant="contained"
            className={classes.uploadButton}
            startIcon={<CloudUploadIcon />}
            onClick={handleOpenUploadModal}
          >
            Upload Report
          </Button>
        )}
      </div>
    )
  }

  const renderTable = () => {
    return (
      <EnhancedTable
        cols={DataTagReportsHeadCells(
          renderFileName,
          renderCreatedDate,
          renderReportPeriod,
          renderGeneratedBy,
          renderSections,
          renderStatus,
          renderExpiryDate,
          renderDownloadAction
        )}
        data={dataTagReports}
        totalCount={totalReportsCount}
        loc={"dataTagReport"}
        currentPage={currentPage}
        recordsPerPage={recordsPerPage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        onChangePage={handleChangePage}
        onTableRowClick={(event, data) => handleOpenReportPopup(event, data)}
      />
    )
  }

  const renderDataTagReportsTable = () => {
    return (
      <>
        {renderSearchBar()}
        <div className="custom-data-grid-main report">
          {renderTable()}
          {selectedRow !== null && PopupContent(selectedRow)}
        </div>
      </>
    );
  }

  const renderMessage = () => {
    return (
      <>
        {snackBarMessage && snackBarMessage.status && snackBarMessage.message && (
          <>
            {errorSnackbar(openSnackBar, handlePopUpClose, snackBarMessage.message, snackBarMessage.status)}
          </>
        )}
        {uploadSnackBarMessage && uploadSnackBarMessage.status && uploadSnackBarMessage.message && (
          <>
            {errorSnackbar(openUploadSnackBar, handleUploadPopUpClose, uploadSnackBarMessage.message, uploadSnackBarMessage.status)}
          </>
        )}
      </>
    )
  }

  return (
    <>
      {loader && (
        <img
          src={Page_Loader}
          alt="loading"
          className="loader-data-source reports datatag-reports-loader"
        ></img>
      )}
      <Backdrop open={downloadLoader} style={{ zIndex: 9999, color: '#fff' }}>
        <div className="download-loader-container">
          <CircularProgress color="inherit" />
          <div className="download-message">Downloading file...</div>
        </div>
      </Backdrop>
      {!loader && renderDataTagReportsTable()}
      {renderMessage()}
      <UploadReportModal 
        open={openUploadModal} 
        onClose={handleCloseUploadModal} 
      />
    </>
  )
}

export default DataTagReports;
